import React, { useState, useCallback } from 'react'
import { Map as ImmutableMap } from 'immutable'
import { Box, Typography, Alert, TextField, IconButton, Tooltip } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import MuiButton from 'gui/components/common/MuiButton'
import type { AiToolsSharedTranscript } from './ai-helpers'

const COPY_SUCCESS_DURATION_MS = 2000

interface AITranscriptProps {
  /** Immutable Map containing content data (must include contentId) */
  content: ImmutableMap<string, unknown>
  /** Whether the form is in view-only mode */
  isViewOnlyMode: boolean
  /** ID of the attached media (used to disable when no feature media) */
  featureMediaId?: number | string | null
  /** Shared with AIMetadataGeneration so edits flow to Generate Metadata */
  sharedTranscript: AiToolsSharedTranscript
}

/**
 * Fetch transcript only and show it in an editable text field with copy and reset.
 * Does not call any AI endpoints—just transcript fetch and local editing.
 */
function AITranscript ({
  content,
  isViewOnlyMode,
  featureMediaId,
  sharedTranscript,
}: AITranscriptProps) {
  const {
    fetchTranscript,
    fetchedTranscript,
    transcriptLoading,
    transcriptError,
    transcriptDraft,
    onTranscriptDraftChange,
  } = sharedTranscript

  const [copySuccess, setCopySuccess] = useState(false)

  const contentId = content.get('contentId') as number | string | undefined

  const handleFetchTranscript = useCallback((): void => {
    if (!contentId) return
    fetchTranscript(contentId)
  }, [contentId, fetchTranscript])

  const handleRetry = useCallback((): void => {
    if (!contentId) return
    fetchTranscript(contentId)
  }, [contentId, fetchTranscript])

  const handleCopyToClipboard = useCallback(async (): Promise<void> => {
    if (!transcriptDraft) return
    try {
      await navigator.clipboard.writeText(transcriptDraft)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), COPY_SUCCESS_DURATION_MS)
    } catch {
      // Clipboard can fail (e.g. permissions); no user feedback for now
    }
  }, [transcriptDraft])

  const isButtonDisabled = !featureMediaId || isViewOnlyMode || transcriptLoading || !contentId

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h6" gutterBottom>
        Transcript
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Fetch the video transcript, edit it if you like, then use Generate Metadata from AI below
        with the same text. No AI processing in this step—just the raw transcript.
      </Typography>

      {transcriptError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <MuiButton color="primary" size="small" onClick={handleRetry}>
              Retry
            </MuiButton>
          }
        >
          {transcriptError}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <MuiButton
          variant="contained"
          color="primary"
          onClick={handleFetchTranscript}
          disabled={isButtonDisabled}
        >
          {transcriptLoading ? 'Fetching transcript…' : 'Fetch transcript'}
        </MuiButton>
      </Box>

      {(Boolean(fetchedTranscript) || transcriptDraft.length > 0) && (
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Transcript ({transcriptDraft.length.toLocaleString()} characters)
            </Typography>
            <Tooltip title={copySuccess ? 'Copied!' : 'Copy to clipboard'}>
              <IconButton
                onClick={handleCopyToClipboard}
                color="primary"
                size="small"
                aria-label="Copy transcript to clipboard"
                disabled={!transcriptDraft}
              >
                {copySuccess ? <CheckIcon /> : <ContentCopyIcon />}
              </IconButton>
            </Tooltip>
          </Box>
          <TextField
            fullWidth
            multiline
            minRows={8}
            maxRows={20}
            value={transcriptDraft}
            onChange={(e) => onTranscriptDraftChange(e.target.value)}
            disabled={isViewOnlyMode || transcriptLoading}
            variant="outlined"
            InputProps={{
              sx: {
                fontFamily: 'monospace',
                fontSize: '0.85rem',
              },
            }}
            sx={{
              '& .MuiInputBase-root': {
                backgroundColor: '#fff',
              },
            }}
          />
          <Box sx={{ mt: 1 }}>
            <MuiButton
              size="small"
              variant="outlined"
              onClick={() => onTranscriptDraftChange(fetchedTranscript ?? '')}
              disabled={isViewOnlyMode || transcriptLoading}
            >
              Reset to original
            </MuiButton>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default AITranscript
